// Настройки → Блокировка: через сколько минут без дела блокировать приложение на этом устройстве
// и ключи доступа (отпечаток, лицо, PIN) для разблокировки.

import { browserSupportsWebAuthn } from '@simplewebauthn/browser'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { authKeys, type AuthState } from '@/api/auth'
import { errorMessage } from '@/api/client'
import { Badge, Button, Card, ErrorText, Input, Modal, Select, useToast } from '@/components/ui'
import { formatDateTime } from '@/lib/format'
import { LOCK_CHOICES, setLockAfter, setLocked, setPasskeyId, setUnlockOptions, useLockState } from '@/lock/store'
import { defaultPasskeyName, lockApi, lockKeys, registerPasskey, UnlockError, type Passkey } from '@/lock/unlock'

import { lockChoiceLabel } from './logic'

const OFF = 'off'

function LockCard() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const { lockAfter } = useLockState()

  const save = useMutation({
    mutationFn: (value: number | null) => lockApi.setLock(value),
    onSuccess: (data) => {
      setLockAfter(data.lock_after_s)
      queryClient.setQueryData<AuthState>(authKeys.state, (old) =>
        old ? { ...old, lock_after_s: data.lock_after_s, locked: false } : old,
      )
      toast.show({
        message: data.lock_after_s ? 'Блокировка включена' : 'Блокировка выключена',
        tone: 'accent',
      })
    },
  })
  const lockNow = useMutation({
    mutationFn: () => lockApi.lockNow(),
    onSuccess: () => setLocked(true),
  })

  const options = [
    { value: OFF, label: 'Не блокировать' },
    ...LOCK_CHOICES.map((s) => ({ value: String(s), label: lockChoiceLabel(s) })),
  ]

  return (
    <Card
      title="Блокировка на этом устройстве"
      subtitle="Приложение закрывается, если его не открывали дольше выбранного времени. Открыть — отпечатком или паролем."
    >
      <div className="grid gap-4 sm:max-w-sm">
        <Select
          label="Блокировать"
          options={options}
          value={lockAfter === null ? OFF : String(lockAfter)}
          disabled={save.isPending}
          onChange={(e) => save.mutate(e.target.value === OFF ? null : Number(e.target.value))}
        />
        <ErrorText error={save.error} />
        {lockAfter !== null ? (
          <div>
            <Button onClick={() => lockNow.mutate()} loading={lockNow.isPending}>
              Заблокировать сейчас
            </Button>
            <ErrorText error={lockNow.error} className="mt-2" />
          </div>
        ) : null}
        <p className="text-sm text-muted">
          Время считается, пока приложение свёрнуто или закрыто. Настройка своя у каждого устройства и
          сохраняется при новом входе.
        </p>
      </div>
    </Card>
  )
}

function AddPasskeyModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [password, setPassword] = useState('')
  const [name, setName] = useState(defaultPasskeyName)

  const add = useMutation({
    mutationFn: () => registerPasskey(password, name),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: lockKeys.passkeys })
      toast.show({ message: 'Ключ добавлен: теперь можно открывать отпечатком', tone: 'accent' })
      close()
    },
    onError: () => setPassword(''),
  })

  function close() {
    setPassword('')
    add.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title="Отпечаток на этом устройстве"
      description="Подтверди паролем, затем устройство попросит отпечаток, лицо или PIN."
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          if (password) add.mutate()
        }}
        noValidate
      >
        <input type="text" name="username" autoComplete="username" value="owner" readOnly hidden />
        <Input
          label="Текущий пароль"
          type="password"
          autoComplete="current-password"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Input label="Название" value={name} maxLength={100} onChange={(e) => setName(e.target.value)} />
        <ErrorText>
          {add.error ? (add.error instanceof UnlockError ? add.error.message : errorMessage(add.error)) : null}
        </ErrorText>
        <Button type="submit" variant="primary" loading={add.isPending} disabled={!password}>
          Добавить
        </Button>
      </form>
    </Modal>
  )
}

function PasskeyRow({ passkey, thisDevice }: { passkey: Passkey; thisDevice: boolean }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const remove = useMutation({
    mutationFn: () => lockApi.deletePasskey(passkey.id),
    onSuccess: () => {
      if (thisDevice) {
        setPasskeyId(null)
        setUnlockOptions(null)
      }
      void queryClient.invalidateQueries({ queryKey: lockKeys.passkeys })
    },
    onError: (error) => toast.show({ message: errorMessage(error), tone: 'danger' }),
  })
  return (
    <li className="flex items-center gap-3 py-2.5">
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-2 text-sm font-medium text-fg">
          <span className="truncate">{passkey.name}</span>
          {thisDevice ? <Badge tone="accent">это устройство</Badge> : null}
        </p>
        <p className="text-xs text-muted">
          Добавлен {formatDateTime(passkey.created_at)}
          {passkey.last_used_at ? ` · открывал ${formatDateTime(passkey.last_used_at)}` : ''}
        </p>
      </div>
      <Button variant="ghost" size="sm" loading={remove.isPending} onClick={() => remove.mutate()}>
        Удалить
      </Button>
    </li>
  )
}

function PasskeysCard() {
  const { passkeyId } = useLockState()
  const [open, setOpen] = useState(false)
  const list = useQuery({ queryKey: lockKeys.passkeys, queryFn: lockApi.passkeys })
  const supported = browserSupportsWebAuthn()
  const keys = list.data ?? []
  const hasThisDevice = keys.some((p) => p.credential_id === passkeyId)

  return (
    <Card
      title="Отпечаток и Face ID"
      subtitle="Ключ доступа хранится в защищённой памяти устройства; отпечаток никуда не передаётся."
    >
      {list.isPending ? <p className="text-sm text-muted">Загружаю…</p> : null}
      <ErrorText error={list.error} />
      {keys.length > 0 ? (
        <ul className="divide-y divide-border">
          {keys.map((p) => (
            <PasskeyRow key={p.id} passkey={p} thisDevice={p.credential_id === passkeyId} />
          ))}
        </ul>
      ) : list.isSuccess ? (
        <p className="text-sm text-muted">Ключей пока нет — разблокировать можно только паролем.</p>
      ) : null}
      {supported ? (
        hasThisDevice ? null : (
          <Button className="mt-3" onClick={() => setOpen(true)}>
            Добавить отпечаток на этом устройстве
          </Button>
        )
      ) : (
        <p className="mt-3 text-sm text-muted">Этот браузер не поддерживает ключи доступа.</p>
      )}
      <AddPasskeyModal open={open} onClose={() => setOpen(false)} />
    </Card>
  )
}

export function LockSettingsPage() {
  return (
    <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
      <LockCard />
      <PasskeysCard />
    </div>
  )
}
